<template>
  <v-container fluid :class="{ 'mt-4': !$vuetify.breakpoint.mobile }" :style="$vuetify.breakpoint.mobile && !filterBy ? 'padding-bottom: 96px' : ''">
    <v-row class="mb-3">
      <v-col cols="12" md="6">
        <v-tabs v-model="filterBy" :grow="$vuetify.breakpoint.mobile">
          <v-tab><v-icon left>mdi-pencil-outline</v-icon>{{ $t('claimforms.tabs.unsigned') }}</v-tab>
          <v-tab><v-icon left>mdi-check</v-icon>{{ $t('claimforms.tabs.signed') }}</v-tab>
        </v-tabs>
      </v-col>

      <v-col cols="12" md="6" v-if="!filterBy && !$vuetify.breakpoint.mobile">
        <div class="text-end">
          <v-btn color="primary" class="mr-2" :disabled="!selectedUnsigned.length" @click="onSignForm(selectedUnsigned[0])">
            <v-icon left>mdi-pencil-outline</v-icon>{{ $t('claimforms.menu.sign') }}
          </v-btn>
          <v-btn outlined :disabled="!selectedUnsigned.length" @click="onCancelForm(selectedUnsigned[0])">
            <v-icon left>mdi-close</v-icon>{{ $t('claimforms.menu.cancel') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <mobile-data-list
      v-if="$vuetify.breakpoint.mobile"
      v-model="selectedUnsigned"
      :items="visibleClaimforms"
      :loading="loading"
      :items-per-page="100"
      :footer-props="footerProps"
      item-key="id"
      :headers="mobileHeaders"
      :show-select="!filterBy"
      single-expand
    >
      <template #item.ityp="{ item }">
        <v-icon v-if="String(item.ityp) === '1'" small>mdi-volume-high</v-icon>
        <v-icon v-else-if="String(item.ityp) === '2'" small>mdi-video</v-icon>
        <span v-else>{{ item.ityp }}</span>
      </template>

      <template #expanded-actions="{ item }">
        <div class="d-flex mt-2 mb-1" style="gap: 8px">
          <v-btn small outlined @click.stop="openRights(item)">
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            {{ $t('claimforms.menu.rights') }}
          </v-btn>
          <v-btn small outlined @click.stop="doDownload(item)">
            <v-icon left small>mdi-tray-arrow-down</v-icon>
            {{ $t('claimforms.menu.download') }}
          </v-btn>
        </div>
      </template>
    </mobile-data-list>

    <v-row v-else>
      <v-col cols="12">
        <data-table-scroll-hint>
        <v-data-table
            :show-select="!filterBy"
            :single-select="true"
            v-model="selectedUnsigned"
            dense
            item-key="id"
            :items="visibleClaimforms"
            :loading="loading"
            :headers="headers"
            :page.sync="currentPage"
            :items-per-page.sync="itemsPerPage"
            :footer-props="footerProps"
            @click:row="openRights"
        >

          <template v-slot:item.actions="{ item }">

            <v-menu v-if="!filterBy">
              <template v-slot:activator="{on, attrs}">
                <v-btn icon v-on="on" v-bind="attrs">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                    v-for="action in getActions(item)"
                    :key="action"
                    @click="onMenuClick(item, action)"
                >
                  <v-list-item-title>
                    {{ $t(`claimforms.menu.${action}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn v-else icon @click.stop="doDownload(item)">
              <v-icon>mdi-tray-arrow-down</v-icon>
              <span v-if="$vuetify.breakpoint.xs">{{ $t('claimforms.menu.download') }}</span>
            </v-btn>

          </template>

        </v-data-table>
        </data-table-scroll-hint>
      </v-col>
    </v-row><!-- v-else -->

    <div v-if="$vuetify.breakpoint.mobile && !filterBy" class="cf-mobile-actions">
      <v-btn color="primary" :disabled="!selectedUnsigned.length" @click="onSignForm(selectedUnsigned[0])">
        <v-icon left>mdi-pencil-outline</v-icon>{{ $t('claimforms.menu.sign') }}
      </v-btn>
      <v-btn outlined :disabled="!selectedUnsigned.length" @click="onCancelForm(selectedUnsigned[0])">
        <v-icon left>mdi-close</v-icon>{{ $t('claimforms.menu.cancel') }}
      </v-btn>
    </div>

    <v-dialog v-model="signForm" :width="$vuetify.breakpoint.mobile ? '95%' : '50%'" max-width="700px">
      <SignForm @close="signForm = false;" v-if="signForm" :item="currentClaimform" @signed="reloadItems"/>
    </v-dialog>

    <v-dialog v-model="rightsListVisible" width="90%" scrollable :max-height="$vuetify.breakpoint.mobile ? '90vh' : undefined">
      <ClaimformRights
          :item="currentClaimform"
          v-if="rightsListVisible"
          @close="rightsListVisible = false;"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import RightsholderInitedDataDrivenMixin from "@/components/mixins/RightsholderInitedDataDrivenMixin";
import SignForm from "@/components/claimforms/SignForm";
import ClaimformRights from "@/components/claimforms/ClaimformRights";
import MobileDataList from "@/components/common/MobileDataList";
import DataTableScrollHint from '@/components/common/DataTableScrollHint.vue';
import downloadFile from "@/utils/downloadFile";

export default {
  name: "ClaimForms",
  components: {ClaimformRights, SignForm, MobileDataList, DataTableScrollHint},
  data() {
    return {
      loading: true,
      currentPage: 1,
      itemsPerPage: 100,
      signForm: false,
      rightsListVisible: false,
      currentClaimform: null,
      filterBy: 0,
      selectedUnsigned: []
    }
  },
  mixins: [RightsholderInitedDataDrivenMixin],
  computed: {
    footerProps() {
      return { itemsPerPageOptions: this.$vuetify.breakpoint.mobile ? [] : [50, 100, 200, 500, 2000, 5000] }
    },
    visibleClaimforms() {
      const f = (status) => (this.filterBy === 0 ? Number(status) === 0 : Number(status) !== 0);
      return this.claimformsList.filter(({signStatus}) => f(signStatus));
    },
    claimformsList() {
      return this.$store.getters["app/claimforms"].list || [];
    },
    mobileHeaders() {
      const h = (value) => ({ value, text: this.$t(`claimforms.columns.${value}`) });
      const isSigned = this.filterBy !== 0;
      const dateField = isSigned ? 'signedDateFormatted' : 'openedDateFormatted';
      return [
        { ...h(dateField), mobile: 'primary' },
        { ...h('id'),      mobile: 'secondary' },
        { ...h('rader'),   mobile: 'tertiary' },
        h('ityp'),
        ...(isSigned ? [{ ...h('signedBy'), stacked: true }, h('openedDateFormatted')] : [h('signedDateFormatted')]),
        h('raderFel'),
        h('raderMak'),
      ];
    },

    headers() {
      const getCol = (value) => ({value, text: this.$t(`claimforms.columns.${value}`)});
      return [
        'rowNumber',
        'id',
        'ityp',
        'signStatus',
        'openedDateFormatted',
        'signedDateFormatted',
        'signedBy',
        'details',
        'rader',
        'raderFel',
        'raderMak'
      ].map(getCol)
        .concat([
          this.filterBy ? {
            value: 'actions',
            sortable: false,
          } : {}]);
    }
  },
  watch: {
    filterBy() {
      this.reloadItems();
    }
  },
  methods: {
    reloadItems() {
      this.loading = true;
      this.$store.dispatch('app/loadClaimforms', {reload: true})
        .finally(() => {
          this.loading = false;
        });
    },

    onSignForm(item) {
      this.currentClaimform = item;
      this.signForm = true;
    },

    onCancelForm(item) {
      const actions = {
        back: this.$t('claimforms.cancel.back'),
        remove: this.$t('claimforms.cancel.remove'),
        cancel: this.$t('app.no')
      }
      this.$dialog.confirm({
        text: this.$t('claimforms.confirmCancel'),
        actions: [{
          text: actions.back,
          color: 'primary'
        }, {
          text: actions.remove,
          color: 'primary'
        }, {
          text: actions.cancel,
          color: 'secondary'
        }]
      })
        .then((response) => {
          // eslint-disable-next-line no-unreachable
          if (response === actions.back) {
            this.cancelForm(item);
          }
          if (response === actions.remove) {
            this.cancelForm(item, true)
          }
        })
    },

    cancelForm(item, remove = false) {
      this.$store.dispatch('app/cancelClaimform', {item, remove});
    },

    openRights(item) {
      this.rightsListVisible = true;
      this.currentClaimform = item;
    },

    doDownload(item) {
      this.currentClaimform = item;
      this.$store.dispatch('app/getClaimformDetailsDownloadUrl', {clnr: item.id, format: 'xlsx'})
        .then((res) => {
          downloadFile((res.filename ? res.filename : 'claims.xlsx'), res.url);
        })
    },

    getActions({signStatus}) {
      return Number(signStatus) ? ['rights','download'] : ['rights', 'sign', 'cancel','download'];
    },

    onMenuClick(item, action) {
      switch (action) {
        case 'rights':
          this.openRights(item);
          break;
        case 'sign':
          this.onSignForm(item);
          break;
        case 'cancel':
          this.onCancelForm(item);
          break;
        case 'download':
          this.doDownload(item);
          break;
      }
    }
  },
}
</script>

<style scoped>
.v-tab {
  text-transform: none !important;
}
@media (max-width: 600px) {
  .container {
    padding-bottom: 96px;
  }
}
.cf-mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px 16px 16px;
  background: linear-gradient(to bottom, transparent, var(--color-viewport-bg) 40%);
  z-index: 5;
  display: flex;
  gap: 8px;
}
.cf-mobile-actions .v-btn:first-child {
  flex: 1;
}
</style>